import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Stethoscope, RefreshCw, Filter } from "lucide-react";
import { toast } from "sonner";

/**
 * Doctor-wise Investigation Report — admin tracker.
 * Rolls up every investigation (OPD / IPD / Walk-in / Manual) by prescribing
 * doctor with per-type breakdown. Backed by
 * GET /api/reports/investigations/by-doctor
 */

const TYPES = ["All", "Lab", "X-Ray", "CT", "MRI", "USG", "ECG", "Echo", "Endoscopy", "EEG", "PFT", "Radiology", "Physiotherapy", "Other"];
const SOURCES = ["All", "opd", "ipd", "walkin", "manual"];

export default function DoctorInvestigationReport() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [rows, setRows] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    doctor_id: "",
    type: "All",
    source: "All",
    from_date: monthStart,
    to_date: today,
  });

  useEffect(() => {
    api.get("/doctors").then(r => setDoctors(r.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v && v !== "All") params[k] = v; });
      const r = await api.get("/reports/investigations/by-doctor", { params });
      setRows(r.data || []);
    } catch (e) { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const t = { total: 0, by_type: {}, amount: 0 };
    for (const r of rows) {
      t.total += r.total || 0;
      t.amount += r.amount || 0;
      for (const [k, v] of Object.entries(r.by_type || {})) t.by_type[k] = (t.by_type[k] || 0) + v;
    }
    return t;
  }, [rows]);

  const typeCols = useMemo(() => {
    const s = new Set();
    rows.forEach(r => Object.keys(r.by_type || {}).forEach(k => s.add(k)));
    return Array.from(s).sort();
  }, [rows]);

  return (
    <div className="space-y-4" data-testid="doctor-inv-report">
      <div>
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Stethoscope className="w-6 h-6 text-blue-700"/> Doctor-wise Investigation Report
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          Every prescribed / referred investigation, grouped by doctor, with per-type breakdown across OPD, IPD, Walk-in and manual orders.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-sm p-3 grid grid-cols-1 md:grid-cols-6 gap-2">
        <div>
          <div className="label-tiny mb-1">Doctor</div>
          <select
            value={f.doctor_id}
            onChange={(e) => setF({ ...f, doctor_id: e.target.value })}
            className="w-full h-9 border rounded-sm text-sm px-2 bg-white"
            data-testid="dwr-doctor"
          >
            <option value="">All doctors</option>
            {doctors.map(d => <option key={d.id} value={d.id}>{d.name} · {d.department}</option>)}
          </select>
        </div>
        <div>
          <div className="label-tiny mb-1">Investigation Type</div>
          <select
            value={f.type}
            onChange={(e) => setF({ ...f, type: e.target.value })}
            className="w-full h-9 border rounded-sm text-sm px-2 bg-white"
            data-testid="dwr-type"
          >
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <div className="label-tiny mb-1">Source</div>
          <select
            value={f.source}
            onChange={(e) => setF({ ...f, source: e.target.value })}
            className="w-full h-9 border rounded-sm text-sm px-2 bg-white"
            data-testid="dwr-source"
          >
            {SOURCES.map(s => <option key={s} value={s}>{s === "All" ? "All sources" : s.toUpperCase()}</option>)}
          </select>
        </div>
        <div>
          <div className="label-tiny mb-1">From</div>
          <Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value })} className="h-9" data-testid="dwr-from"/>
        </div>
        <div>
          <div className="label-tiny mb-1">To</div>
          <Input type="date" value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} className="h-9" data-testid="dwr-to"/>
        </div>
        <div className="flex items-end gap-1">
          <Button onClick={load} variant="outline" className="rounded-sm h-9 flex-1" data-testid="dwr-refresh">
            <RefreshCw className="w-4 h-4 mr-1"/> Refresh
          </Button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Doctors" value={rows.length}/>
        <Kpi label="Total Investigations" value={totals.total}/>
        <Kpi label="Lab" value={totals.by_type.Lab || 0}/>
        <Kpi label="Imaging (X-Ray+CT+MRI+USG)" value={(totals.by_type["X-Ray"] || 0) + (totals.by_type.CT || 0) + (totals.by_type.MRI || 0) + (totals.by_type.USG || 0)}/>
        <Kpi label="Est. Amount ₹" value={totals.amount.toLocaleString("en-IN")}/>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-sm overflow-auto">
        <table className="w-full text-xs" data-testid="dwr-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left">
              <th className="p-2">Doctor</th>
              <th>Department</th>
              <th className="text-right">Total</th>
              {typeCols.map(t => <th key={t} className="text-right">{t}</th>)}
              <th className="text-right">OPD</th>
              <th className="text-right">IPD</th>
              <th className="text-right">Walk-in</th>
              <th className="text-right pr-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8 + typeCols.length} className="p-6 text-center text-slate-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8 + typeCols.length} className="p-6 text-center text-slate-500">
                No investigations match these filters.
              </td></tr>
            )}
            {!loading && rows.map(r => (
              <tr key={r.doctor_id} className="border-b hover:bg-slate-50" data-testid={`dwr-row-${r.doctor_id}`}>
                <td className="p-2 font-medium">
                  {r.doctor_name}
                  {r.doctor_id === "__unassigned__" && <Badge variant="outline" className="ml-1 text-[9px]">Walk-in</Badge>}
                </td>
                <td className="text-slate-600">{r.department || "—"}</td>
                <td className="text-right font-mono font-semibold text-blue-800">{r.total}</td>
                {typeCols.map(t => (
                  <td key={t} className="text-right font-mono">
                    {r.by_type?.[t] || 0}
                  </td>
                ))}
                <td className="text-right font-mono text-slate-600">{r.by_source?.opd || 0}</td>
                <td className="text-right font-mono text-slate-600">{r.by_source?.ipd || 0}</td>
                <td className="text-right font-mono text-slate-600">{r.by_source?.walkin || 0}</td>
                <td className="text-right font-mono pr-3">{(r.amount || 0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border border-blue-200 bg-white rounded-sm p-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono text-lg font-semibold text-blue-800 mt-0.5">{value}</div>
    </div>
  );
}
